import { DueDisplayOptions, GroupByMode, ID } from '@griply/types';
import { buildTodayDefaultFilter } from '@griply/utils';
import { useTasksByFilterDocument } from 'features/tasks';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { formatDurationLabel } from 'shared/utils/format-duration-label';
import { ProvidedTaskFilterList } from 'shared/views/task-filter-list';

import { useTodayCustomOrderByGrouping } from '../../hooks/use-today-custom-order-by-grouping';
import { useTodayFilterDocument } from '../../hooks/use-today-filter-document';
import { useTodayOnReorder } from '../../hooks/use-today-on-reorder';
import { getTaskDuration } from '../../utils/get-task-duration';
import { CompletedTasks } from '../completed-tasks';
import { ExternalCalendarEvents } from '../external-calendar-events';
import { PageTopBar } from '../page-top-bar';
import * as Styled from './grouped-list.style';

const groupSettings = {
  [GroupByMode.Priority]: {
    showEmptyGroups: true,
    displayDue: [DueDisplayOptions.Past],
  },
  [GroupByMode.Type]: {
    showEmptyGroups: true,
    displayDue: [DueDisplayOptions.Past],
  },
  [GroupByMode.Deadline]: {
    showEmptyDate: true,
    showSingleDeadlineAsNoGrouping: true,
    displayDue: [DueDisplayOptions.Past],
  },
  [GroupByMode.Goal]: {
    displayDue: [DueDisplayOptions.Past],
  },
  [GroupByMode.LifeArea]: {
    displayDue: [DueDisplayOptions.Past],
  },
  [GroupByMode.Section]: {
    displayDue: [DueDisplayOptions.Past],
  },
  [GroupByMode.Tag]: {
    displayDue: [DueDisplayOptions.Past],
  },
  [GroupByMode.DayPart]: {
    displayDue: [DueDisplayOptions.Past],
  },
  [GroupByMode.None]: {
    displayDue: [DueDisplayOptions.Past],
  },
};

export type GroupedListProps = {
  onCalendar: () => void;
  calendarOpen?: boolean;
};

export const GroupedList: React.FC<GroupedListProps> = ({
  onCalendar,
  calendarOpen,
}) => {
  const user = useUser();
  const today = useToday();
  const updateUserSettings = useUpdateUserSettings();
  const filterDocument = useTodayFilterDocument();
  const scrollElement = useRef<HTMLDivElement>(null);

  const defaultFilterDocument = useMemo(
    () => buildTodayDefaultFilter(today),
    [today],
  );
  const tasks = useTasksByFilterDocument(filterDocument, {
    includeTaskWithAction: true,
    excludeHiddenTodoHabits: true,
  });

  const totalDurationLabel = useMemo(() => {
    const totalDuration = tasks.reduce<number>(
      (acc, task) => acc + getTaskDuration(task, today),
      0,
    );

    return totalDuration > 0
      ? formatDurationLabel(totalDuration, false)
      : undefined;
  }, [tasks, today]);

  const groupMode = filterDocument.groupByMode ?? GroupByMode.Deadline;

  const customOrder = useTodayCustomOrderByGrouping(groupMode);
  const onReorder = useTodayOnReorder(groupMode);

  const onSectionCollapse = useCallback(
    (collapsedSections: ID[]) =>
      updateUserSettings({
        filters: {
          today: {
            collapsedSections,
          },
        },
      }),
    [updateUserSettings],
  );

  return (
    <>
      <PageTopBar
        onCalendar={onCalendar}
        calendarOpen={calendarOpen}
        totalDurationLabel={totalDurationLabel}
      />

      <Styled.Content ref={scrollElement}>
        <Styled.MetaContainer>
          <ExternalCalendarEvents
            show={!user?.settings?.todayHideDesktopExternalCalendarEvents}
          />
        </Styled.MetaContainer>

        <ProvidedTaskFilterList
          filterDocument={filterDocument}
          defaultFilterDocument={defaultFilterDocument}
          onReorder={onReorder}
          scrollElement={scrollElement}
          customOrder={customOrder}
          onSectionCollapse={onSectionCollapse}
          includeTaskWithAction
          excludeHiddenTodoHabits
          groupSettings={groupSettings}
          defaultNewTaskDate={today}
          includeDuration
        />

        <CompletedTasks />
      </Styled.Content>
    </>
  );
};
